import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  ArrowLeft, MapPin, Home, Maximize, BedDouble, Bath, 
  Layers, Car, Droplets, TreePine, Fence, Calendar,
  Flame, ChevronLeft, ChevronRight, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ContactForm from "@/components/ContactForm";
import axios from "axios";

const API = "/api";

const DPEScale = ({ label, value, classLetter, type }) => {
  const colors = {
    A: '#319834', B: '#33cc31', C: '#cbfc34', 
    D: '#fbfe06', E: '#fbcc05', F: '#fc9935', G: '#fc0205'
  };
  const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
  
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-zinc-700">{label}</p>
      <div className="flex flex-col gap-0.5">
        {letters.map((letter, idx) => {
          const width = 40 + idx * 10;
          const isActive = letter === classLetter;
          return (
            <div 
              key={letter}
              className="flex items-center gap-2"
            >
              <div 
                className={`h-5 flex items-center justify-end px-2 text-xs font-bold ${isActive ? 'ring-2 ring-zinc-900' : ''}`}
                style={{ 
                  width: `${width}%`, 
                  backgroundColor: colors[letter],
                  color: ['C', 'D', 'E'].includes(letter) ? '#333' : '#fff'
                }}
              >
                {letter}
              </div>
              {isActive && value && (
                <span className="text-sm font-medium text-zinc-700">
                  {value} {type === 'dpe' ? 'kWh/m²/an' : 'kg CO₂/m²/an'}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function PropertyDetailPage() {
  const { reference } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [showGallery, setShowGallery] = useState(false);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`${API}/properties/${reference}`);
        setProperty(response.data);
      } catch (error) {
        console.error("Error fetching property:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [reference]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const nextImage = () => {
    if (property?.images) {
      setCurrentImage((prev) => (prev + 1) % property.images.length);
    }
  };

  const prevImage = () => {
    if (property?.images) {
      setCurrentImage((prev) => (prev - 1 + property.images.length) % property.images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-zinc-200 w-32" />
            <div className="aspect-[16/9] bg-zinc-200" />
            <div className="h-10 bg-zinc-200 w-3/4" />
            <div className="h-6 bg-zinc-200 w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <Home className="h-16 w-16 mx-auto mb-4 text-zinc-300" />
          <h2 className="font-['Outfit'] text-2xl font-medium text-zinc-900 mb-2">
            Bien non trouvé
          </h2>
          <p className="text-zinc-500 mb-6">
            Ce bien n'existe pas ou n'est plus disponible.
          </p>
          <Link to="/ventes">
            <Button className="bg-[#D62965] hover:bg-[#A61E4E] rounded-none">
              Voir tous les biens
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const features = [
    { icon: Maximize, label: "Surface", value: `${property.surface} m²`, show: true },
    { icon: BedDouble, label: "Chambres", value: property.bedrooms, show: property.bedrooms > 0 },
    { icon: Home, label: "Pièces", value: property.rooms, show: property.rooms > 0 },
    { icon: Bath, label: "Salle(s) de bain", value: property.bathrooms, show: property.bathrooms > 0 },
    { icon: Layers, label: "Niveaux", value: property.levels, show: property.levels > 1 },
    { icon: Maximize, label: "Terrain", value: property.surface_terrain ? `${property.surface_terrain.toLocaleString()} m²` : null, show: !!property.surface_terrain },
    { icon: Calendar, label: "Année", value: property.year_built, show: property.year_built && property.year_built > 1000 },
    { icon: Car, label: "Garage", value: "Oui", show: property.has_garage },
    { icon: Car, label: "Parking", value: "Oui", show: property.has_parking },
    { icon: Droplets, label: "Piscine", value: "Oui", show: property.has_pool },
    { icon: TreePine, label: "Jardin", value: "Oui", show: property.has_garden },
    { icon: Fence, label: "Terrasse", value: "Oui", show: property.has_terrace },
    { icon: Flame, label: "Chauffage", value: property.heating, show: !!property.heating },
  ].filter(f => f.show);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="property-detail-page">
      {/* Full Screen Gallery */}
      {showGallery && property.images && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center" data-testid="fullscreen-gallery">
          <Button 
            variant="ghost" 
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setShowGallery(false)}
            data-testid="close-gallery-btn"
          >
            <X className="h-8 w-8" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
            onClick={prevImage}
            data-testid="gallery-prev-btn"
          >
            <ChevronLeft className="h-10 w-10" />
          </Button>
          <img 
            src={property.images[currentImage]} 
            alt={`Image ${currentImage + 1}`}
            className="max-h-[90vh] max-w-[90vw] object-contain"
          />
          <Button 
            variant="ghost" 
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
            onClick={nextImage}
            data-testid="gallery-next-btn"
          >
            <ChevronRight className="h-10 w-10" />
          </Button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
            {currentImage + 1} / {property.images.length}
          </div>
        </div>
      )}

      {/* Back Link */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/ventes" className="inline-flex items-center text-sm text-zinc-600 hover:text-[#D62965] transition-colors" data-testid="back-to-listings">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux annonces
          </Link>
        </div>
      </div>

      {/* Image Gallery */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {property.images && property.images.length > 0 ? (
            <div className="grid grid-cols-4 gap-2 h-[380px] lg:h-[490px] min-h-[380px] max-h-[490px] overflow-hidden">
              {/* Main Image */}
              <div 
                className="col-span-4 lg:col-span-3 relative cursor-pointer overflow-hidden"
                onClick={() => setShowGallery(true)}
                data-testid="main-image"
              >
                <img 
                  src={property.images[0]} 
                  alt={property.title}
                  className="w-full h-full object-cover gallery-image"
                />
                <div className="absolute bottom-4 right-4">
                  <Badge className="bg-zinc-900/80 text-white">
                    {property.images.length} photos
                  </Badge>
                </div>
              </div>
              {/* Thumbnails */}
              <div className="hidden lg:grid grid-rows-3 gap-2 h-full min-h-0">
                {property.images.slice(1, 4).map((img, idx) => (
                  <div 
                    key={idx}
                    className="relative cursor-pointer overflow-hidden min-h-0"
                    onClick={() => { setCurrentImage(idx + 1); setShowGallery(true); }}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img 
                      src={img} 
                      alt={`Image ${idx + 2}`}
                      className="w-full h-full object-cover gallery-image"
                    />
                    {idx === 2 && property.images.length > 4 && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="text-white font-medium">+{property.images.length - 4}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[400px] bg-zinc-100 flex items-center justify-center">
              <Home className="h-20 w-20 text-zinc-300" />
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <Badge className="bg-zinc-900 text-white rounded-none uppercase tracking-wider">
                  {property.property_type}
                </Badge>
                <span className="text-sm text-zinc-500">Réf: {property.reference}</span>
              </div>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl tracking-tight font-medium text-zinc-900 mb-3" data-testid="property-title">
                {property.title}
              </h1>
              <div className="flex items-center gap-2 text-zinc-600">
                <MapPin className="h-4 w-4 text-[#00B5AD]" />
                <span>{property.city} ({property.postal_code})</span>
              </div>
            </div>

            {/* Features Grid */}
            <div className="bg-white border border-zinc-200 p-6" data-testid="features-grid">
              <h2 className="font-['Outfit'] text-xl font-medium text-zinc-900 mb-6">
                Caractéristiques
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-zinc-50">
                    <feature.icon className="h-5 w-5 text-[#00B5AD]" />
                    <div>
                      <p className="text-xs text-zinc-500">{feature.label}</p>
                      <p className="font-medium text-zinc-900">{feature.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white border border-zinc-200 p-6" data-testid="property-description">
              <h2 className="font-['Outfit'] text-xl font-medium text-zinc-900 mb-4">
                Description
              </h2>
              <div className="prose prose-zinc max-w-none">
                {property.description.split('\n').map((paragraph, idx) => (
                  paragraph.trim() && <p key={idx} className="text-zinc-600 leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
            </div>

            {/* DPE / GES */}
            {(property.dpe_class || property.ges_class) && (
              <div className="bg-white border border-zinc-200 p-6" data-testid="dpe-ges-section">
                <h2 className="font-['Outfit'] text-xl font-medium text-zinc-900 mb-6">
                  Diagnostic énergétique
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {property.dpe_class && (
                    <DPEScale 
                      label="Consommation énergétique (DPE)" 
                      value={property.dpe_value}
                      classLetter={property.dpe_class}
                      type="dpe"
                    />
                  )}
                  {property.ges_class && (
                    <DPEScale 
                      label="Émissions de gaz à effet de serre (GES)" 
                      value={property.ges_value}
                      classLetter={property.ges_class}
                      type="ges"
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 space-y-6">
              {/* Price Card */}
              <div className="bg-white border border-zinc-200 p-6" data-testid="price-card">
                <p className="text-sm text-zinc-500 mb-1">Prix de vente</p>
                <p className="font-['Outfit'] text-3xl font-bold text-zinc-900 tracking-tight mb-2">
                  {formatPrice(property.price)}
                </p>
              </div>

              {/* Contact Form */}
              <div className="bg-white border border-zinc-200 p-6" data-testid="contact-sidebar">
                <h3 className="font-['Outfit'] text-lg font-medium text-zinc-900 mb-4">
                  Intéressé par ce bien ?
                </h3>
                <ContactForm 
                  propertyReference={property.reference} 
                  propertyTitle={property.title}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
